import { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getInventoryAction } from 'redux/actions/productActions'
import { toTitleCase } from 'Utils/functions'
import { sortInventoryData } from 'Utils/Helpers/sortInventoryData'

/**
 * Hook personalizado para manejar la lógica de inventario
 * @param {Object} options - Opciones de configuración
 * @returns {Object} - Estado y funciones para manejar el inventario
 */
const useInventoryProduct = () => {
  const [productId, setProductId] = useState(null)
  const [inventory, setInventory] = useState([])
  const [sortInventory, setSortInventory] = useState([])
  const [loadingInventary, setLoadingInventary] = useState(false)
  const [hiddenColumnsInventory, setHiddenColumnsInventory] = useState([])

  const dispatch = useDispatch()
  const companyId = useSelector(state => state.company.CompanyId)
  const user = useSelector(state => state.login.user)

  /**
   * Obtiene la disponibilidad de productos por almacén
   * @param {string} companyId - ID de la compañía
   * @param {string} productIdParam - ID del producto (opcional, usa el estado si no se proporciona)
   * @returns {Promise} - Promesa con la respuesta
   */
  const fetchWarehousesAvailability = async (companyId, productIdParam) => {
    // Usar el productId del parámetro si se proporciona, de lo contrario usar el del estado
    const currentProductId = productIdParam || productId

    const payload = {
      ProductId: currentProductId,
      CustomerId: user.CustomerInfo.CustomerId,
      GlobalExecution: false,
      PageNumber: 1,
      PageSize: 200,
      CompanyId: companyId,
      Latitude: 0,
      Longitude: 0,
    }

    const response = await dispatch(getInventoryAction(payload))

    if (response && response.data) {
      const warehouses = [...response.data.Data].sort((a, b) => {
        if (a.CompanyId === companyId && b.CompanyId !== companyId) return -1
        if (a.CompanyId !== companyId && b.CompanyId === companyId) return 1

        return a.WarehouseCode.localeCompare(b.WarehouseCode)
      })

      const warehousesFormatted = formatInventoryData(warehouses)
      setSortInventory(warehousesFormatted)
      setInventory(warehousesFormatted)
    } else {
      console.error('Error:', response.toString())
      throw new Error(response.toString())
    }
  }

  /**
   * Obtiene la disponibilidad de productos en todos los almacenes
   * @param {string} productIdParam - ID del producto (opcional, usa el estado si no se proporciona)
   */
  const fetchAllWarehousesAvailability = async productIdParam => {
    setInventory([])
    setLoadingInventary(true)

    // Usar el productId del parámetro si se proporciona, de lo contrario usar el del estado
    const currentProductId = productIdParam || productId

    await fetchWarehousesAvailability(companyId, currentProductId)

    setLoadingInventary(false)
  }

  /**
   * Formatea los datos de inventario para la tabla
   * @param {Array} data - Datos de inventario
   * @returns {Array} - Datos formateados
   */
  const formatInventoryData = data => {
    return data.map(office => {
      return {
        CompanyId: office.CompanyId,
        Tienda: toTitleCase(office.WarehouseName),
        Costo: office.Cost,
        Código: office.WarehouseCode,
        Disponible: office?.AvailableQty || 0,
        'En Existencia': office?.InStockQty || 0,
        Comprometido: office?.ReservedQty || 0,
        'En Compras': office?.OnOrderQty || 0,
        Requisición: office?.RequisitionQty || 0,
        Traslado: office?.TransferQty || 0,
        Total: office?.TotalQty || 0,
      }
    })
  }

  /**
   * Obtiene las columnas ocultas para la tabla
   * @param {Array} hiddenColumns - Configuración de columnas ocultas
   * @returns {Array} - Nombres de columnas ocultas
   */
  const getArrayHiddenColumns = hiddenColumns => {
    const hiddenColumnsCopy = hiddenColumns.filter(column =>
      column.Status === false ? column.ColumnName : false
    )
    return hiddenColumnsCopy.map(column => column.ColumnName)
  }

  // Función para aplicar ordenamiento
  const applySorting = useCallback(
    (columnId, orderType) => {
      if (!companyId || !columnId) return

      try {
        const sortedData = sortInventoryData({
          inventory,
          companyId,
          attribute: columnId,
          orderType,
        })

        setSortInventory(sortedData)
      } catch (error) {
        console.error('Error al ordenar datos:', error)
        setSortInventory(inventory)
      }
    },
    [inventory, companyId]
  )

  const handleSortChange = useCallback(
    sortBy => {
      if (sortBy.length > 0) {
        const { id: columnId, desc } = sortBy[0]
        const orderType = desc ? 'desc' : 'asc'
        applySorting(columnId, orderType)
      }
    },
    [applySorting]
  )

  // Cargar datos cuando cambia el productId
  useEffect(() => {
    if (productId) {
      fetchAllWarehousesAvailability(productId)
    }
  }, [productId, companyId])

  return {
    setProductId,
    inventoryData: sortInventory,
    loadingInventary,
    hiddenColumnsInventory,
    setHiddenColumnsInventory,
    getArrayHiddenColumns,
    fetchAllWarehousesAvailability,
    handleSortChange,
  }
}

export default useInventoryProduct
